/** Customer-facing labels for organizer permission modules (no raw slugs in UI). */

export type PermissionCatalogItem = { key: string; label: string };

export type PermissionCategory = { category: string; permissions: PermissionCatalogItem[] };

export const ORGANIZER_PERMISSION_CATALOG: PermissionCategory[] = [
  {
    category: 'Overview',
    permissions: [{ key: 'overview.view', label: 'Overview' }],
  },
  {
    category: 'Events',
    permissions: [
      { key: 'events.view', label: 'View events' },
      { key: 'events.create', label: 'Create events' },
      { key: 'events.update', label: 'Update events' },
      { key: 'events.delete', label: 'Delete events' },
    ],
  },
  {
    category: 'Participants & Teams',
    permissions: [
      { key: 'participants.manage', label: 'Manage participants' },
      { key: 'teams.manage', label: 'Manage teams' },
    ],
  },
  {
    category: 'Competition',
    permissions: [
      { key: 'jury.manage', label: 'Jury management' },
      { key: 'results.publish', label: 'Publish results' },
    ],
  },
  {
    category: 'Certificates',
    permissions: [
      { key: 'certificates.issue', label: 'Issue certificates' },
      { key: 'certificates.download', label: 'Download certificates' },
    ],
  },
  {
    category: 'Reports & Analytics',
    permissions: [
      { key: 'reports.view', label: 'View reports' },
      { key: 'reports.export', label: 'Export reports' },
      { key: 'analytics.view', label: 'Analytics' },
    ],
  },
  {
    category: 'Communication',
    permissions: [{ key: 'email.send', label: 'Email campaigns' }],
  },
  {
    category: 'Finance',
    permissions: [
      { key: 'settlements.manage', label: 'Settlements' },
      { key: 'billing.manage', label: 'Billing' },
    ],
  },
  {
    category: 'Platform Fee',
    permissions: [
      { key: 'platform_fee.enabled', label: 'Enable platform fee' },
      { key: 'platform_fee.configuration', label: 'Configure fee type and value' },
      { key: 'platform_fee.payer', label: 'Choose whether organizer or attendee pays' },
    ],
  },
  {
    category: 'Activity Modules',
    permissions: [
      { key: 'activity_modules.manage', label: 'Create and manage activities' },
      { key: 'activity_modules.registration', label: 'Activity registration and capacity' },
      { key: 'activity_modules.attendance', label: 'Activity attendance and tracking' },
    ],
  },
  {
    category: 'Attendees Limit',
    permissions: [
      { key: 'attendees_limit.maximum', label: 'Set maximum attendee limit' },
      { key: 'attendees_limit.unlimited', label: 'Allow unlimited attendees' },
      { key: 'attendees_limit.waitlist', label: 'Enable waitlist and automatic registration closure' },
    ],
  },
  {
    category: 'Results',
    permissions: [
      { key: 'results.calculate', label: 'Enter and calculate results' },
      { key: 'results.publish_category_team', label: 'Publish category or team results' },
      { key: 'results.export', label: 'Export results' },
    ],
  },
  {
    category: 'Registration Features',
    permissions: [
      { key: 'registration_features.custom_forms', label: 'Custom registration forms' },
      { key: 'registration_features.approval_waitlist', label: 'Registration approval and waitlist' },
      { key: 'registration_features.export_confirmation', label: 'Registration export and confirmation' },
    ],
  },
  {
    category: 'Feedback',
    permissions: [
      { key: 'feedback.forms', label: 'Create feedback forms' },
      { key: 'feedback.collect', label: 'Collect event or activity feedback' },
      { key: 'feedback.analytics_export', label: 'Feedback analytics and export' },
    ],
  },
  {
    category: 'QR Checking',
    permissions: [
      { key: 'qr_checking.generate_tickets', label: 'Generate QR tickets' },
      { key: 'qr_checking.check_in_out', label: 'QR check-in and check-out' },
      { key: 'qr_checking.scan_history', label: 'Scan history and analytics' },
    ],
  },
  {
    category: 'Category Ticketing',
    permissions: [
      { key: 'category_ticketing.create_categories', label: 'Create ticket categories' },
      { key: 'category_ticketing.pricing_capacity', label: 'Category pricing and capacity' },
      { key: 'category_ticketing.management', label: 'Category ticket management' },
    ],
  },
  {
    category: 'Jury Dashboard',
    permissions: [
      { key: 'jury_dashboard.manage_members', label: 'Manage jury members' },
      { key: 'jury_dashboard.evaluate_submissions', label: 'Assign and evaluate submissions' },
      { key: 'jury_dashboard.publish_scores', label: 'Jury scores and result publishing' },
    ],
  },
  {
    category: 'In-App Notifications',
    permissions: [
      { key: 'in_app_notifications.send', label: 'Send in-app notifications' },
      { key: 'in_app_notifications.schedule_target', label: 'Schedule and target notifications' },
      { key: 'in_app_notifications.history_read_status', label: 'Notification history and read status' },
    ],
  },
  {
    category: 'Team Discussions',
    permissions: [
      { key: 'team_discussions.channels', label: 'Team discussion channels' },
      { key: 'team_discussions.replies_mentions_attachments', label: 'Replies, mentions, and attachments' },
      { key: 'team_discussions.moderation_history', label: 'Moderation and message history' },
    ],
  },
  {
    category: 'Support',
    permissions: [{ key: 'support.view', label: 'Support access' }],
  },
];

const KNOWN_MODULE_LABELS: Record<string, string> = {
  'module.overview.view': 'Overview Dashboard',
  'module.event_info.manage': 'Event Information & Details',
  'module.participants.manage': 'Participant Management',
  'module.email_campaigning.send': 'Email Campaigns & Broadcasts',
  'module.jury.manage': 'Jury & Evaluation Management',
  'module.results.publish': 'Result Publishing',
  'module.certificates.issue': 'Certificate Issuance',
  'module.reports.view': 'Reports & Analytics',
  'module.settlements.manage': 'Settlements & Payouts',
  'module.activity_modules.manage': 'Activity & Session Modules',
  'module.attendees_limit.manage': 'Attendee Limits & Waitlists',
  'module.registration_features.manage': 'Custom Registration Forms',
  'module.feedback.manage': 'Feedback Collection',
  'module.qr_checking.manage': 'QR Ticketing & Check-In',
  'module.category_ticketing.manage': 'Category Ticketing',
  'module.jury_dashboard.manage': 'Jury Evaluation Dashboard',
  'module.in_app_notifications.manage': 'In-App Notifications',
  'module.team_discussions.manage': 'Team Discussion Channels',
  'module.support.manage': 'Dedicated Support Access',
  'module.quiz.manage': 'Quiz & Trivia Contests',
  'module.coding.manage': 'Coding Challenges',
  'module.pitch.manage': 'Pitch & Ideathons',
  'module.workshop.manage': 'Workshop & Training Modules',
  'module.simulation.manage': 'Simulation & Model UN',
  'module.analytics.view': 'Analytics & Funnels',
  'module.communications.send': 'Communication Center',
};

export function labelForPermissionKey(key: string): string {
  if (!key || typeof key !== 'string') return '';

  const clean = key.trim();
  if (KNOWN_MODULE_LABELS[clean]) {
    return KNOWN_MODULE_LABELS[clean];
  }

  // Canonicalize to dot-separated format (handles hyphens, underscores, spaces)
  // e.g. "module event_info manage" or "module-event-info-manage" -> "module.event_info.manage"
  const dotCandidate = clean
    .toLowerCase()
    .replace(/^module[.\s\-_]+/, 'module.')
    .replace(/[\s\-]+/g, '_')
    .replace(/^module_/, 'module.');

  if (KNOWN_MODULE_LABELS[dotCandidate]) {
    return KNOWN_MODULE_LABELS[dotCandidate];
  }

  // Also test standard dotted equivalent: "module.event_info.manage"
  const standardDot = clean.toLowerCase().replace(/[\s\-_]+/g, '.');
  if (KNOWN_MODULE_LABELS[standardDot]) {
    return KNOWN_MODULE_LABELS[standardDot];
  }

  for (const group of ORGANIZER_PERMISSION_CATALOG) {
    const found = group.permissions.find(
      (p) => p.key === clean || p.key === dotCandidate || p.key === standardDot
    );
    if (found) return found.label;
  }

  // If prefixed with module, strip it and look up or title-case
  const stripped = clean.replace(/^(module)[.\s\-_]+/i, '');
  if (KNOWN_MODULE_LABELS[`module.${stripped}`]) {
    return KNOWN_MODULE_LABELS[`module.${stripped}`];
  }

  for (const group of ORGANIZER_PERMISSION_CATALOG) {
    const found = group.permissions.find((p) => p.key === stripped);
    if (found) return found.label;
  }

  // Humanize string: split by delimiters, omit redundant "module" word, title case
  const words = stripped
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .filter((w) => w.toLowerCase() !== 'module');

  if (words.length === 0) return clean;

  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function allPermissionKeys(): string[] {
  return ORGANIZER_PERMISSION_CATALOG.flatMap((g) => g.permissions.map((p) => p.key));
}
